import React from "react";
import { Sparkles } from "lucide-react";
import { formatGBP, KID_PRICE_STEPS_PENCE } from "@/lib/marketplace";

/**
 * BestOfferPricing — Iteration 301b. The dual Best Offer price controls
 * shared by the Kid Zone sell form and the parent /sell form so both
 * surfaces stay in perfect parity:
 *
 *   • List Price  — preset chips + "Or custom £0.00" input
 *   • Secret Floor Price (Best Offer mode only) — preset chips
 *     (filtered to ≤ list price) + "Or custom £0.00" input
 *
 * Chips ⇄ inputs stay in sync both ways: tapping a chip populates the
 * custom input; typing a non-preset amount deselects every chip in that
 * row (typing a preset amount re-highlights the matching chip).
 *
 * Guardrail: floor > list renders the inline error
 * "Secret floor price cannot be higher than list price" — callers also
 * disable their submit button via the returned `floorTooHigh` pattern
 * (compute `bestOffer && pricePence > 0 && floorPence > pricePence`).
 *
 * Props:
 *   bestOffer     — false renders list-price controls only
 *   pricePence / floorPence — integer pence (0 = unset)
 *   onPriceChange(pence) / onFloorChange(pence)
 *   testIdPrefix  — "kid-form" | "sell-form"
 */
export default function BestOfferPricing({
  bestOffer,
  pricePence,
  floorPence,
  onPriceChange,
  onFloorChange,
  testIdPrefix,
}) {
  const floorTooHigh = bestOffer && pricePence > 0 && floorPence > pricePence;
  const toPence = (raw) => Math.max(0, Math.round(parseFloat(raw || "0") * 100));
  return (
    <>
      <div className="mt-3 text-xs font-semibold text-slate-500" data-testid={`${testIdPrefix}-price-label`}>
        {bestOffer ? "List price (a guide for buyers)" : "Tap a price"}
      </div>
      <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-price-chips`}>
        {KID_PRICE_STEPS_PENCE.map((p) => {
          const picked = pricePence === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onPriceChange(p)}
              className={`px-3 py-1.5 rounded-full text-sm font-bold border transition ${picked ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-800 border-slate-200 hover:border-emerald-500"}`}
              data-testid={`${testIdPrefix}-price-chip-${p}`}
              aria-pressed={picked}
            >
              {formatGBP(p / 100)}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-custom-price`}>
        <label htmlFor={`${testIdPrefix}-price-custom`} className="text-xs font-semibold text-slate-500">Or custom</label>
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white overflow-hidden focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/25">
          <span className="pl-3 pr-1 text-sm font-bold text-slate-500">£</span>
          <input
            id={`${testIdPrefix}-price-custom`}
            type="number"
            inputMode="decimal"
            min="0.50"
            step="0.50"
            placeholder="0.00"
            value={pricePence ? (pricePence / 100).toString() : ""}
            onChange={(e) => onPriceChange(toPence(e.target.value))}
            className="w-24 py-1.5 pr-3 text-sm font-mono font-bold text-slate-900 bg-transparent outline-none"
            data-testid={`${testIdPrefix}-price-custom-input`}
          />
        </div>
      </div>
      {bestOffer && (
        <>
          <div className="mt-3 text-xs font-semibold text-slate-500" data-testid={`${testIdPrefix}-floor-label`}>
            Secret floor price — offers at or above it are accepted instantly
          </div>
          <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-floor-chips`}>
            {KID_PRICE_STEPS_PENCE.filter((p) => !pricePence || p <= pricePence).map((p) => {
              const picked = floorPence === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => onFloorChange(p)}
                  className={`px-3 py-1.5 rounded-full text-sm font-bold border transition ${picked ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-800 border-slate-200 hover:border-amber-400"}`}
                  data-testid={`${testIdPrefix}-floor-chip-${p}`}
                  aria-pressed={picked}
                >
                  {formatGBP(p / 100)}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-floor-custom-price`}>
            <label htmlFor={`${testIdPrefix}-floor-custom`} className="text-xs font-semibold text-slate-500">Or custom</label>
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white overflow-hidden focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-400/25">
              <span className="pl-3 pr-1 text-sm font-bold text-slate-500">£</span>
              <input
                id={`${testIdPrefix}-floor-custom`}
                type="number"
                inputMode="decimal"
                min="0.50"
                step="0.50"
                placeholder="0.00"
                value={floorPence ? (floorPence / 100).toString() : ""}
                onChange={(e) => onFloorChange(toPence(e.target.value))}
                className="w-24 py-1.5 pr-3 text-sm font-mono font-bold text-slate-900 bg-transparent outline-none"
                data-testid={`${testIdPrefix}-floor-custom-input`}
              />
            </div>
          </div>
          {floorTooHigh && (
            <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid={`${testIdPrefix}-floor-error`} role="alert">
              Secret floor price cannot be higher than list price
            </div>
          )}
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 inline-flex items-center gap-2" data-testid={`${testIdPrefix}-pwyc-hint`}>
            <Sparkles className="w-3.5 h-3.5" /> Best Offer — offers at or above your secret floor are accepted instantly and paid from the buyer's Piggy Bank. No parent wait!
          </div>
        </>
      )}
    </>
  );
}
